import { Box } from '@mui/material';
import { useMemo } from 'react';

import { Switch } from '@/components/form/baseInputs/Switch/Switch';
import { useOptionalDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { zIndexes } from '@/styles/zIndexes';

interface BdgDeathOrderSwitchProps {
  /** Offset from the top of the floating container. */
  top?: number;
}

/**
 * Death-order Switch rendered above the form-view diagram for BDG-aware
 * entities. Reads & mutates the active scenario via
 * `BdgDiagramSyncState` so this component can live outside the
 * `EntityDiagramProvider` tree.
 *
 * Renders nothing when the diagram isn't BDG-aware or the household has
 * fewer than 2 primary clients. For survivorship policies the toggle
 * still renders so the user can visualize either death order — the
 * OFF position falls back to the first primary client since there's no
 * privileged trigger client.
 */
export function BdgDeathOrderSwitch({ top = 56 }: BdgDeathOrderSwitchProps) {
  const syncContext = useOptionalDispositiveDiagramSyncContext();
  const { primaryClients } = useHouseholdDetailsContext();
  const bdg = syncContext?.bdgDiagramSyncState ?? null;
  const defaultTriggerClientId = bdg?.defaultTriggerClientId;

  const { defaultClient, alternate } = useMemo(() => {
    const list = primaryClients ?? [];
    if (list.length < 2) {
      return { defaultClient: null, alternate: null };
    }
    const def =
      (defaultTriggerClientId
        ? list.find((c) => c.id === defaultTriggerClientId)
        : null) ?? list[0]!;
    const alt = list.find((c) => c.id !== def.id) ?? null;
    return { defaultClient: def, alternate: alt };
  }, [defaultTriggerClientId, primaryClients]);

  if (!bdg) return null;
  if (!defaultClient || !alternate) return null;

  const isOn = bdg.selectedPrimaryClientId === alternate.id;

  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        left: 0,
        zIndex: zIndexes.MENU,
      }}
    >
      <Switch
        label={`Show ${alternate.displayGivenName} dies first`}
        labelPosition="right"
        value={isOn}
        onChange={(e) => {
          bdg.setSelectedPrimaryClientId(
            e.target.checked ? alternate.id : defaultClient.id
          );
        }}
        testId="bdg-death-order-switch-form"
      />
    </Box>
  );
}
